import { customFetch } from '@agos/api-client'
import type { StaffProfile } from '@agos/api-client/model'
import { Moon, Sun } from 'lucide-react'
import { useEffect, useRef } from 'react'

import { tt } from '../i18n'
import { schemeIsDark, useTheme } from './theme-context'

/**
 * A moon and a sun, saved against the person who pressed it - §9.642, owner O41.
 *
 * **The owner's report**: *"in manager on top right add moon/sun to switch between light/dark mode, because
 * that «whole franchise» even tho its set to dark mode, sometimes showing white mode... its annoyin as fuck.
 * And we need to fix that ,and also add that switcher (also one in the driver app)"*, then *"And make sure it
 * will be saved to each employee their custom setting for it"*.
 *
 * **In `@agos/shared` rather than in the manager app**, because the driving surface needs the same control and
 * `@agos/manager-driving` cannot import from `apps/manager`. Both depend on this package, and one control in
 * one place is what stops the two drifting into different behaviour on the same preference.
 *
 * **`customFetch` rather than the generated mutation hook.** This file lives in the package that owns the
 * theme, which already imports `customFetch` for the design save; reaching for `usePatchAuthMeProfile` would
 * make `@agos/shared` depend on the generated client's React layer for one request. The write is a single
 * field and needs no cache invalidation, because the context is already the source of truth on screen.
 *
 * **Applied before it is saved, and the save may fail without undoing it.** A theme is read at a glance and a
 * till's network is not reliable; a control that waited for a round trip would feel broken, and one that
 * reverted on failure would flicker back at somebody who had just pressed it. `localStorage` carries the
 * choice across a reload either way, so the worst a failed save costs is that a *different* device does not
 * learn about it.
 *
 * **Two states, not three.** `auto` stays a franchise answer - §10A.326 - and a person reaches it by never
 * having overridden it. A button with a moon on it cannot honestly offer a third meaning, so clearing a
 * personal choice belongs on the profile screen rather than in this icon; `TODO.md` carries it.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const theme = useTheme()
  const adopted = useRef(false)

  /* **Adopt what the server holds, once, so a second device learns about the choice.** `localStorage` is what
   * paints before the bundle and it is per browser; the column is what makes the preference the person's
   * rather than the device's - a driver who switched on their phone should find the same scheme on the till
   * they sign into next.
   *
   * **Once, guarded by a ref**: without it, adopting would set state, re-render, and read again. And only
   * when the server disagrees with what is already applied, so the ordinary load writes nothing.
   *
   * It costs one small read per mount on a surface that already polls, and ignoring the failure is right:
   * the local choice is already on screen and a reload keeps it. */
  useEffect(() => {
    if (adopted.current) return
    adopted.current = true
    /* **The generated type, not a hand-written shape** - §9.416's rule, and its ratchet refused the first
     * draft of this line by name: the server declares `StaffProfile`, so declaring a narrower one here is a
     * second description of the same payload that stops agreeing with it the moment the field moves. */
    void customFetch<StaffProfile>('/api/v1/auth/me/profile')
      .then((profile) => {
        const stored = profile?.themePreference
        const theirs = stored === 'light' || stored === 'dark' ? stored : null
        if (theirs !== theme.myScheme) theme.setMyScheme(theirs)
      })
      .catch(() => undefined)
    // `theme` is read inside and must not re-run this: the ref guards it, and listing it would make the
    // adoption depend on every theme change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dark = schemeIsDark(theme.mode, theme.myScheme)
  const next = dark ? 'light' : 'dark'

  function choose() {
    theme.setMyScheme(next)
    void customFetch<unknown>('/api/v1/auth/me/profile', {
      method: 'PATCH',
      body: JSON.stringify({ themePreference: next }),
    }).catch(() => undefined)
  }

  return (
    <button
      type="button"
      onClick={choose}
      data-theme-toggle
      data-theme-toggle-state={dark ? 'dark' : 'light'}
      /* The label says what pressing it does rather than what the screen is now: a control named for its
         current state reads as a status line, and somebody looking for the light mode scans for the word. */
      aria-label={dark ? tt('Prepnúť na svetlý režim') : tt('Prepnúť na tmavý režim')}
      title={dark ? tt('Prepnúť na svetlý režim') : tt('Prepnúť na tmavý režim')}
      className={
        className ??
        'w-9 h-9 rounded-xl border border-border bg-card flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors'
      }
    >
      {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>
  )
}
